import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { displayWarningMessage, normalizeText } from "../../../shared/utils";
import { Form as FormAnt } from "antd";
import { InfoContext } from "../../../shared/contexts";
import { PageHeader } from "../../../shared/components/MainLayout/components";
import { InputFactory, RowSize } from "../../../shared/components";
import { ContactIndexTable } from "./components";
import { getInputsFilter } from "../shared/utils/inputsContextIndex";
import { DownloadOutlined } from "@ant-design/icons";
import { SUBFOLDERS_TYPES } from "../shared/enums/documents-subfolders";
import VirtualOfficeService from "../../../shared/services/virtualOffice";
import { downloadFile } from "../shared/utils/downloadFileS3";

interface ContactIndexProps {
    title: string;
    subFolder: SUBFOLDERS_TYPES;
}

export const ContactIndex: FC<ContactIndexProps> = ({ title, subFolder }) => {

    const [form] = FormAnt.useForm();
    const { setTitle } = useContext(InfoContext);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [contactIndexList, setContactIndexList] = useState<any[]>([]);
    const [areasCombo, setAreasCombo] = useState<any>([]);

    useEffect(() => {
        const init = async () => {
            setTitle(title);
            await getAreaCombo();
        };
        init();
    }, []);
    
    useEffect(() => {
        if (areasCombo.length > 0) {
            getIndexContactList();
        }
    }, [areasCombo]);
    

    const getAreaCombo = useCallback(async () => {
        try {
            const response = await VirtualOfficeService.getAreaCombo();
            const areaCombo = response.sectores.map(area => ({
                value: area.codigoArea,
                label: area.nombreSector
            }));
            setAreasCombo(areaCombo);

        } catch (error) { }
    }, [setAreasCombo]);

    const getIndexContactDocument = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await VirtualOfficeService.getFormsList(subFolder);

            const indice = response.find(item => {
                const name = item.key.toLowerCase().replace(/\s+/g, "");
                return name.includes("indicedecontactos.pdf");
            });

            if (indice?.url) {
                await downloadFile(indice.url, 'IndiceDeContactos.pdf');
            } else {
                displayWarningMessage('No es posible descargar el documento solicitado.')
            }
        } catch (error) {
        } finally {
            setIsLoading(false);
        }
    }, [subFolder]);

    const initialStateFilters = useMemo(() => ({
        area: undefined,
        keyword: undefined,
    }), []);

    const getIndexContactList = useCallback(async () => {
        setIsLoading(true);
        const filters = form.getFieldsValue();
    
        try {
            const contactIndexResponse = await VirtualOfficeService.getIndexContacts();
            const areaMap = Object.fromEntries(
                areasCombo.map((a: { value: any; label: any; }) => [a.value, a.label])
            );
    
            let filteredContacts = contactIndexResponse.contactos.map(c => ({
                ...c,
                nombreArea: areaMap[c.codigoArea] ?? ""
            }));
        
            if (filters.area) {
                const selectedAreas = filters.area.map((a: string | undefined) => normalizeText(a));
                filteredContacts = filteredContacts.filter(contact =>
                    selectedAreas.includes(normalizeText(contact.codigoArea))
                );
            }
            if (filters.keyword) {
                const kw = normalizeText(filters.keyword);
                filteredContacts = filteredContacts.filter((contact) =>
                    normalizeText(contact.nombre).includes(kw)
                );
            }
    
            setContactIndexList(filteredContacts);
        } finally {
            setIsLoading(false);
        }
    
    }, [areasCombo, form]);

    const cleanFilter = () => {
        getIndexContactList();
    }

    const actions = useMemo(
        () => [
            {
                label: "Descargar",
                className: "btn-accent-outline-barbuss",
                icon: <DownloadOutlined />,
                onClick: getIndexContactDocument,
            },
        ],
        [getIndexContactDocument]
    );

    return (
        <>
            <PageHeader actions={actions} />
            <RowSize rowProps={{ gutter: [0, 0] }} className="mb-2">
                {<InputFactory
                    form={form}
                    items={getInputsFilter(areasCombo)}
                    onFilter={getIndexContactList}
                    isLoading={false}
                    initialState={initialStateFilters}
                    onClear={cleanFilter}
                />}
            </RowSize>
            <ContactIndexTable
                contactIndexData={contactIndexList}
                isLoading={isLoading}
            />
        </>
    );
}
